import React, { useState, useEffect } from 'react';
import { getAllCustomers, getEligibleCustomers, getTopCustomers, checkSmsBalanceViaProxy, sendSmsViaProxy } from '../firebase';
import '../styles/SendSms.css';

const SendSmsPageArabic = () => {
  // SMS and customer state
  const [messageText, setMessageText] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState('custom');
  const [customPhoneNumbers, setCustomPhoneNumbers] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  
  // Templates management
  const [templates, setTemplates] = useState([
    { id: 1, name: 'الترحيب', text: 'مرحبًا بكم في برنامج الولاء! لديك {points} نقطة حتى الآن.' },
    { id: 2, name: 'العروض', text: 'عرض خاص: احصل على نقاط مضاعفة على مشترياتك التالية! ساري حتى {date}.' },
    { id: 3, name: 'تحديث النقاط', text: 'لديك الآن {points} نقطة في حساب الولاء الخاص بك. تحتاج {remaining} نقطة إضافية للحصول على مكافأة!' },
    { id: 4, name: 'أهلية المكافأة', text: 'تهانينا! لديك نقاط كافية لاستبدال مكافأة. قم بزيارتنا للمطالبة بها!' }
  ]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Message preview and variables
  const [previewText, setPreviewText] = useState('');
  const [messageVariables, setMessageVariables] = useState({
    points: 500,
    date: new Date().toLocaleDateString('ar-PS'),
    remaining: 500
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [charactersRemaining, setCharactersRemaining] = useState(160);
  const [smsCount, setSmsCount] = useState(1);
  const [showVariablesEditor, setShowVariablesEditor] = useState(false);
  
  // Message sent indicator
  const [showMessageSentIndicator, setShowMessageSentIndicator] = useState(false);
  
  // Stats
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [recipientCount, setRecipientCount] = useState(0);
  const [balanceInfo, setBalanceInfo] = useState(null);
  
  // Check SMS balance via Cloud Function proxy
  const checkSmsBalance = async (providerId = 1) => {
    try {
      setLoading(true);

      const result = await checkSmsBalanceViaProxy(providerId);

      // Parse the XML response
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(result.xml, "text/xml");

      // Extract balance information - note the typo in 'availabebalance'
      const balanceElement = xmlDoc.getElementsByTagName("availabebalance")[0];

      if (balanceElement && balanceElement.textContent) {
        const balance = balanceElement.textContent;
        // Show the balance information
        setBalanceInfo({
          balance,
          provider: providerId === 1 ? 'جوال' : providerId === 2 ? 'وطنية' : 'دولي'
        });
        setSuccess(`Current SMS Balance (${providerId === 1 ? 'Jawwal' : providerId === 2 ? 'Wataniya' : 'International'}): ${balance} messages`);
      } else {
        setError("Could not retrieve balance information");
      }
    } catch (err) {
      console.error("Error checking SMS balance:", err);
      //setError(`Failed to check balance: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch customers when component mounts
  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const allCustomers = await getAllCustomers();
        const eligible = await getEligibleCustomers();
        const top = await getTopCustomers(5);
        
        setCustomers(allCustomers || []);
        setEligibleCustomers(eligible || []);
        setTopCustomers(top || []);
        setFilteredCustomers(allCustomers || []);
      } catch (err) {
        console.error("Error fetching customers:", err);
        setError("فشل تحميل بيانات العملاء. يرجى المحاولة مرة أخرى.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchCustomerData();
    
    // Check balance on component mount
    checkSmsBalance(1);
  }, []);
  
  // Update character count and SMS segments
  useEffect(() => {
    // Calculate remaining characters and SMS segments
    const getMessageDetails = (text) => {
      if (!text) {
        return { remaining: 160, count: 0 };
      }
      
      // Arabic SMS uses UCS-2 encoding: 70 chars for 1 SMS, 67 chars per segment for multi-part SMS
      const firstSegmentLength = 70;
      const additionalSegmentLength = 67;
      
      const length = text.length;
      
      if (length <= firstSegmentLength) {
        return { remaining: firstSegmentLength - length, count: 1 };
      } else {
        const additionalSegments = Math.ceil((length - firstSegmentLength) / additionalSegmentLength);
        const totalSegments = 1 + additionalSegments;
        const lastSegmentRemaining = additionalSegmentLength - ((length - firstSegmentLength) % additionalSegmentLength);
        
        return { remaining: lastSegmentRemaining, count: totalSegments };
      }
    };
    
    const { remaining, count } = getMessageDetails(previewText);
    setCharactersRemaining(remaining);
    setSmsCount(count);
    
    // Calculate estimated cost
    // This is just an example - adjust pricing based on your actual SMS costs
    const estimatedCostPerSms = 0.06; // ₪0.06 per SMS segment
    const totalRecipientsCount = getSelectedRecipientsCount();
    const totalCost = totalRecipientsCount * count * estimatedCostPerSms;
    
    setEstimatedCost(totalCost);
    setRecipientCount(totalRecipientsCount);
  }, [previewText, selectedRecipients, customPhoneNumbers, selectedCustomers]);
  
  // Update preview text when message or template changes
  useEffect(() => {
    const updatePreviewText = () => {
      let text = messageText;
      
      // Replace variables in the text
      Object.keys(messageVariables).forEach(key => {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        text = text.replace(regex, messageVariables[key]);
      });
      
      setPreviewText(text);
    };
    
    updatePreviewText();
  }, [messageText, messageVariables]);
  
  // Auto-hide message sent indicator after 5 seconds
  useEffect(() => {
    let timer;
    if (showMessageSentIndicator) {
      timer = setTimeout(() => {
        setShowMessageSentIndicator(false);
      }, 5000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showMessageSentIndicator]);
  
  // Get count of selected recipients
  const getSelectedRecipientsCount = () => {
    switch (selectedRecipients) {
      case 'all':
        return customers.length;
      case 'eligible':
        return eligibleCustomers.length;
      case 'top':
        return topCustomers.length;
      case 'custom':
        // Count non-empty phone numbers
        return customPhoneNumbers.split(',')
          .map(num => num.trim())
          .filter(num => num.length > 0).length;
      case 'selected':
        return selectedCustomers.length;
      default:
        return 0;
    }
  };
  
  // Search customers
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query) {
      setFilteredCustomers(customers);
      return;
    }
    
    const filtered = customers.filter(customer => 
      customer.name.toLowerCase().includes(query.toLowerCase()) || 
      customer.id.includes(query)
    );
    
    setFilteredCustomers(filtered);
  };
  
  // Toggle customer selection
  const toggleCustomerSelection = (customerId) => {
    if (selectedCustomers.includes(customerId)) {
      setSelectedCustomers(prevSelected => prevSelected.filter(id => id !== customerId));
    } else {
      setSelectedCustomers(prevSelected => [...prevSelected, customerId]);
    }
  };
  
  // Select template handler
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setMessageText(template.text);
  };
  
  // Handle variable change
  const handleVariableChange = (key, value) => {
    setMessageVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Format phone number to international format for SMS API
  const formatPhoneForSms = (phone) => {
    // Remove any spaces, dashes, or other characters
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');

    // If the phone starts with 0, replace it with the Palestinian country code 970
    if (cleaned.startsWith('0')) {
      cleaned = '970' + cleaned.substring(1);
    }

    // If it starts with +, remove the +
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }

    return cleaned;
  };

  // Reset form after successful send
  const resetForm = () => {
    setMessageText('');
    setPreviewText('');
    setSelectedTemplate(null);
    
    if (selectedRecipients === 'selected') {
      setSelectedCustomers([]);
    }
    
    if (selectedRecipients === 'custom') {
      setCustomPhoneNumbers('');
    }
    
    setShowVariablesEditor(false);
  };

  // Function to send SMS with proper error/success handling
  // Function to send SMS with proper error/success handling
  const handleSendSms = async () => {
    if (!messageText.trim()) {
      setError("الرجاء إدخال نص الرسالة");
      return;
    }
    
    if (recipientCount === 0) {
      setError("لم يتم اختيار مستلمين. الرجاء اختيار مستلم واحد على الأقل.");
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Collect phone numbers based on recipient selection
      let phoneNumbers = [];
      
      switch (selectedRecipients) {
        case 'all':
          phoneNumbers = customers.map(c => c.id);
          break;
        case 'eligible':
          phoneNumbers = eligibleCustomers.map(c => c.id);
          break;
        case 'top':
          phoneNumbers = topCustomers.map(c => c.id);
          break;
        case 'custom':
          phoneNumbers = customPhoneNumbers.split(',')
            .map(num => num.trim())
            .filter(num => num.length > 0);
          break;
        case 'selected':
          phoneNumbers = selectedCustomers;
          break;
        default:
          break;
      }
      
      // Send messages in batches via Cloud Function proxy
      const batchSize = 20;
      const batches = [];

      for (let i = 0; i < phoneNumbers.length; i += batchSize) {
        batches.push(phoneNumbers.slice(i, i + batchSize));
      }

      let successCount = 0;
      let failureCount = 0;

      for (const batch of batches) {
        const promises = batch.map(async phone => {
          try {
            // Format phone number to international format (e.g., 0597533023 -> 970597533023)
            const formattedPhone = formatPhoneForSms(phone);

            console.log(`Sending SMS to ${phone} (formatted: ${formattedPhone})`);

            const result = await sendSmsViaProxy(formattedPhone, previewText);
            const responseText = result.xml;
            console.log(`Response for ${formattedPhone}:`, responseText);

            // Parse the XML response
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(responseText, "text/xml");

            // Look for status code directly
            const statusElements = xmlDoc.getElementsByTagName("Status");

            if (statusElements && statusElements.length > 0) {
              const status = statusElements[0].textContent;

              // Status 1 means success according to documentation
              if (status === "1") {
                successCount++;
                console.log(`Message to ${formattedPhone} sent successfully (Status: 1)`);
              } else {
                failureCount++;
                console.error(`Message to ${formattedPhone} failed with status ${status}`);
              }
            } else if (responseText.includes("<Bulk>")) {
              // Fallback logic if we can't find status element but have <Bulk> tag
              successCount++;
              console.log(`Message to ${formattedPhone} assumed successful (contains <Bulk> tag)`);
            } else {
              failureCount++;
              console.error(`Cannot determine status for ${formattedPhone}: ${responseText}`);
            }
          } catch (error) {
            failureCount++;
            console.error(`Exception for ${phone}:`, error);
          }
        });

        // Wait for all promises in the batch to resolve
        await Promise.allSettled(promises);

        // Add delay between batches
        if (batches.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      if (successCount > 0 || phoneNumbers.length > 0) {
        const message = `تم ارسال الرسائل`;
        
        setSuccess(message);
        
        // Check updated balance
        await checkSmsBalance(1);
        
        // Show message sent indicator
        setShowMessageSentIndicator(true);
        
        // Reset form
        resetForm();
      } else {
        //setError("فشل إرسال أي رسائل. يرجى التحقق من اتصالك والمحاولة مرة أخرى.");
      }
    } catch (err) {
      console.error("خطأ في إرسال الرسائل القصيرة:", err);
      setError(`فشل إرسال الرسائل: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="send-sms-page rtl-support">
      <div className="page-title">
        <h2>إرسال رسائل SMS</h2>
        <p className="subtitle">إرسال رسائل نصية إلى عملاء برنامج الولاء</p>
      </div>
      
      {/* Message Sent Indicator */}
      {showMessageSentIndicator && (
        <div className="message-sent-indicator">
          <div className="indicator-icon">✓</div>
          <div className="indicator-text">تم إرسال الرسالة بنجاح!</div>
        </div>
      )}
      
      {/* Error and success messages */}
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}
      
      {success && (
        <div className="success-message">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="close-btn">×</button>
        </div>
      )}
      
      {/* Balance info */}
      {balanceInfo && (
        <div className="balance-info-card">
          <div className="balance-header">
            <h3>معلومات الرصيد</h3>
          </div>
          <div className="balance-content">
            <div className="balance-item">
              <span className="balance-label">مزود الخدمة:</span>
              <span className="balance-value">{balanceInfo.provider}</span>
            </div>
            <div className="balance-item">
              <span className="balance-label">رصيد الرسائل:</span>
              <span className="balance-value">{balanceInfo.balance} رسالة</span>
            </div>
          </div>
          <div className="balance-actions">
            <button 
              className="balance-button"
              onClick={() => checkSmsBalance(1)}
              disabled={loading}
            >
              تحديث رصيد جوال
            </button>
            <button 
              className="balance-button"
              onClick={() => checkSmsBalance(2)}
              disabled={loading}
            >
              تحديث رصيد وطنية
            </button>
          </div>
        </div>
      )}
      
      {/* Main content grid */}
      <div className="sms-grid">
        <div className="message-section">
          <div className="card">
            <h3 className="section-title">محتوى الرسالة</h3>
            
            {/* Message editor */}
            <div className="message-editor">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="أدخل نص الرسالة هنا..."
                rows={6}
              ></textarea>
              
              <div className="message-counter">
                <span className={charactersRemaining < 10 ? 'warning' : ''}>
                  {charactersRemaining} حرف متبقي
                </span>
                <span className="sms-count">
                  {smsCount} مقطع
                </span>
              </div>
              
              {/* Variables editor toggle */}
              {messageText.includes('{') && (
                <button 
                  className="variables-toggle-button"
                  onClick={() => setShowVariablesEditor(!showVariablesEditor)}
                >
                  {showVariablesEditor ? 'إخفاء المتغيرات' : 'تعديل المتغيرات'}
                </button>
              )}
              
              {/* Variables editor */}
              {showVariablesEditor && (
                <div className="variables-editor">
                  <h4>متغيرات الرسالة</h4>
                  <div className="variables-grid">
                    {Object.keys(messageVariables).map(key => (
                      <div key={key} className="variable-item">
                        <label>{key === 'points' ? 'النقاط' : key === 'date' ? 'التاريخ' : 'المتبقي'}:</label>
                        <input
                          type={key === 'date' ? 'date' : 'text'}
                          value={messageVariables[key]}
                          onChange={(e) => handleVariableChange(key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Message preview */}
            <div className="message-preview">
              <h4>معاينة</h4>
              <div className="preview-bubble">
                <p>{previewText || 'ستظهر معاينة الرسالة هنا'}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="recipients-section">
          <div className="card">
            <h3 className="section-title">المستلمون</h3>
            
            {/* Recipient selector */}
            <div className="recipient-selector">
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="recipients"
                    value="custom"
                    checked={selectedRecipients === 'custom'}
                    onChange={() => setSelectedRecipients('custom')}
                  />
                  أرقام هواتف مخصصة
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="recipients"
                    value="all"
                    checked={selectedRecipients === 'all'}
                    onChange={() => setSelectedRecipients('all')}
                  />
                  جميع العملاء ({customers.length})
                </label>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="recipients"
                    value="eligible"
                    checked={selectedRecipients === 'eligible'}
                    onChange={() => setSelectedRecipients('eligible')}
                  />
                  المؤهلون للمكافآت ({eligibleCustomers.length})
                </label>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="recipients"
                    value="top"
                    checked={selectedRecipients === 'top'}
                    onChange={() => setSelectedRecipients('top')}
                  />
                  كبار العملاء ({topCustomers.length})
                </label>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="recipients"
                    value="selected"
                    checked={selectedRecipients === 'selected'}
                    onChange={() => setSelectedRecipients('selected')}
                  />
                  عملاء محددون ({selectedCustomers.length})
                </label>
              </div>
            </div>
            
            {/* Custom phone numbers input */}
            {selectedRecipients === 'custom' && (
              <div className="custom-numbers">
                <label>أدخل أرقام الهواتف (مفصولة بفواصل):</label>
                <textarea
                  value={customPhoneNumbers}
                  onChange={(e) => setCustomPhoneNumbers(e.target.value)}
                  placeholder="059xxxxxxx, 056xxxxxxx, 054xxxxxxx"
                  rows={3}
                ></textarea>
                <p className="hint">أدخل أرقام الهواتف المحمولة الفلسطينية، مفصولة بفواصل</p>
              </div>
            )}
            
            {/* Customer selector */}
            {selectedRecipients === 'selected' && (
              <div className="customer-selector">
                <div className="search-container">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="ابحث عن العملاء بالاسم أو رقم الهاتف"
                    className="search-input"
                  />
                </div>
                
                <div className="customers-list">
                  {loading ? (
                    <div className="loading-spinner">جاري تحميل العملاء...</div>
                  ) : filteredCustomers.length > 0 ? (
                    filteredCustomers.map(customer => (
                      <div 
                        key={customer.id} 
                        className={`customer-item ${selectedCustomers.includes(customer.id) ? 'selected' : ''}`}
                        onClick={() => toggleCustomerSelection(customer.id)}
                      >
                        <div className="select-checkbox">
                          {selectedCustomers.includes(customer.id) ? '✓' : ''}
                        </div>
                        <div className="customer-info">
                          <span className="customer-name">{customer.name}</span>
                          <span className="customer-phone">{customer.id}</span>
                        </div>
                        <div className="customer-points">
                          <span className="points-value">{customer.totalPoints}</span>
                          <span className="points-label">نقطة</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">لم يتم العثور على عملاء</div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Send button and summary */}
          <div className="card send-summary">
            <div className="summary-info">
              <div className="summary-item">
                <span className="summary-label">المستلمون:</span>
                <span className="summary-value">{recipientCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">مقاطع SMS لكل رسالة:</span>
                <span className="summary-value">{smsCount}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">إجمالي عدد الرسائل:</span>
                <span className="summary-value">{recipientCount * smsCount}</span>
              </div>
            </div>
            
            <button 
              className="send-button"
              onClick={handleSendSms}
              disabled={loading || recipientCount === 0 || !messageText.trim()}
            >
              {loading ? 'جاري الإرسال...' : `إرسال ${recipientCount > 1 ? 'الرسائل' : 'الرسالة'}`}
            </button>
            
            <p className="disclaimer">
              سيتم إرسال الرسائل عبر خدمة حضارة للرسائل القصيرة. تطبق أسعار الرسائل القصيرة القياسية.
            </p>
          </div>
        </div>
      </div>
      
      {/* Add CSS styles for the message sent indicator */}
      <style jsx>{`
        .message-sent-indicator {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #4CAF50;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          animation: slideDown 0.3s ease-out forwards;
        }
        
        .indicator-icon {
          font-size: 24px;
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .indicator-text {
          font-size: 16px;
          font-weight: 600;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default SendSmsPageArabic;